import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Typography,
  Stack
} from '@mui/material';
import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import CategoriesTable from '../../components/CategoriesTable';
import { createCategory, updateCategory } from '../../services/categoryService';
import './BusinessSettings/BusinessSettings.css';

interface Category {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const CategoriesSettings: React.FC = () => {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    is_active: true
  });

  // Завантаження категорій
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/categories');
      if (!response.ok) {
        throw new Error(t('errors.loadFailed', 'categories'));
      }
      const data = await response.json();
      setCategories(data.categories || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic', 'categories'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (id: number, data: Partial<Category>) => {
    try {
      await updateCategory(id, data);
      await fetchCategories();
    } catch (error) {
      throw new Error(t('errors.saveFailed', 'categories'));
    }
  };

  const handleSaveService = async (id: string, data: Partial<{name: string, description?: string, is_active: boolean, category_id?: number}>) => {
    const token = localStorage.getItem('auth_token');
    const isNewService = !id; // Empty ID means it's a new service

    const response = await fetch(`/api/v1/services${isNewService ? '' : '/' + id}`, {
      method: isNewService ? 'POST' : 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(t('errors.saveFailed', 'categories'));
    }

    // The CategoriesTable component will handle refreshing its own services data
    // We don't need to do anything here as the table manages its internal state
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) return;

    try {
      await createCategory(newCategory);
      setNewCategory({ name: '', description: '', is_active: true });
      setShowAddForm(false);
      await fetchCategories();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic', 'categories'));
    }
  };

  const handleCancelAdd = () => {
    setNewCategory({ name: '', description: '', is_active: true });
    setShowAddForm(false);
  };

  // Сортуємо категорії за назвою в алфавітному порядку
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="business-settings">
      <div className="settings-header">
        <h2>{t('title', 'categories')}</h2>
        <p>{t('subtitle', 'categories')}</p>
      </div>

      <div className="settings-content">
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(true)}
            disabled={showAddForm}
          >
            {t('addButton', 'categories')}
          </Button>
        </Box>

        {showAddForm && (
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('add.title', 'categories')}
            </Typography>

            <Stack spacing={3}>
              <TextField
                fullWidth
                label={t('add.nameLabel', 'categories')}
                placeholder={t('add.namePlaceholder', 'categories')}
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                required
              />

              <TextField
                fullWidth
                label={t('add.descriptionLabel', 'categories')}
                placeholder={t('add.descriptionPlaceholder', 'categories')}
                multiline
                rows={3}
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={newCategory.is_active}
                    onChange={(e) => setNewCategory(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label={t('add.activeLabel', 'categories')}
              />

              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleCancelAdd}
                >
                  {t('add.cancel', 'categories')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CheckIcon />}
                  onClick={handleAddCategory}
                  disabled={!newCategory.name.trim()}
                >
                  {t('add.submit', 'categories')}
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}

        <CategoriesTable
          categories={sortedCategories}
          onSave={handleSaveCategory}
          onSaveService={handleSaveService}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CategoriesSettings;
